/**
 * Tryout Registration Status Utility
 *
 * Purpose: Determine whether a tryout event is currently accepting registrations
 * based on the registration window dates and event status.
 *
 * Uses computeTryoutStatus() to derive the real status from dates rather than
 * trusting the stored status column, which can become stale if dates are changed.
 * Only 'cancelled' and 'closed' are trusted from the stored status since those
 * are explicitly set by an admin.
 *
 * Used by:
 *   - Authenticated registration API (register/route.ts)
 *   - Registration page frontend (register/page.tsx)
 *   - Program catalog page (programs/[gymSlug]/ProgramCatalogClient.tsx) — calls
 *     this helper too, and takes its timezone from the SAME getOrgTimezone the
 *     registration API uses (via the browse payload), so the catalog's Register
 *     button and the registration page agree for every gym, not just Eastern ones.
 */

import { computeTryoutStatus } from './computeTryoutStatus';

/** Which rule decided the answer, so a screen can word it without parsing `reason`. */
export type TryoutRegistrationCode =
  | 'open'
  | 'cancelled'
  | 'closed_by_gym'
  | 'completed'
  | 'not_open_yet'
  | 'window_closed';

export interface TryoutRegistrationCheck {
  isOpen: boolean;
  reason: string;
  code: TryoutRegistrationCode;
}

interface TryoutEventForRegistrationCheck {
  status: string;
  event_date: string;
  registration_opens_at?: string | null;
  registration_closes_at?: string | null;
  tryout_event_days?: { event_date: string }[] | null;
}

/**
 * Determines if a tryout event is currently accepting registrations.
 *
 * `timeZone` is the GYM's IANA timezone and is required (GH #5084). It is passed
 * straight through to computeTryoutStatus, which decides "has the last tryout
 * day passed?" — a question about the gym's calendar, not the server's. See that
 * file for why a default argument would be the bug again.
 *
 * Logic:
 *   1. If status is 'cancelled' or 'closed' → blocked (admin explicitly set these)
 *   2. Recompute status from dates to check if truly completed
 *   3. If registration_opens_at is set and now is before it → not yet open
 *   4. If registration_closes_at is set and now is after it → closed
 *   5. Otherwise → open (status 'upcoming' or 'open' + within date window)
 */
export function isTryoutRegistrationOpen(
  event: TryoutEventForRegistrationCheck,
  timeZone: string
): TryoutRegistrationCheck {
  const { status, registration_opens_at, registration_closes_at } = event;

  // Only trust admin-set terminal statuses
  if (status === 'cancelled') {
    return { isOpen: false, reason: 'This evaluation has been cancelled', code: 'cancelled' };
  }

  if (status === 'closed') {
    return { isOpen: false, reason: 'Registration has been closed for this evaluation', code: 'closed_by_gym' };
  }

  // Recompute status from actual dates instead of trusting stored 'completed'
  const computedStatus = computeTryoutStatus(event, timeZone);

  if (computedStatus === 'completed') {
    return { isOpen: false, reason: 'This evaluation has already been completed', code: 'completed' };
  }

  const now = new Date();

  if (registration_opens_at) {
    const opensAt = new Date(registration_opens_at);
    if (now < opensAt) {
      return { isOpen: false, reason: 'Registration has not opened yet', code: 'not_open_yet' };
    }
  }

  if (registration_closes_at) {
    const closesAt = new Date(registration_closes_at);
    if (now > closesAt) {
      return { isOpen: false, reason: 'Registration has closed', code: 'window_closed' };
    }
  }

  return { isOpen: true, reason: 'Registration is open', code: 'open' };
}
